import { useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  Col,
  Drawer,
  Dropdown,
  Form,
  Layout,
  MenuProps,
  message,
  Row,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import PageHeader from "../../../../../components/page-header/PageHeader";
import "./learning-progress.scss";
import { useEffect, useState } from "react";
import {
  ArrowLeftOutlined,
  CheckCircleTwoTone,
  EditOutlined,
  ExportOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import {
  exportAttendanceStudentByCourse,
  getAttendanceHistory,
  getAttendanceStudentByCourse,
  studentProgressDetail,
  studentProgressOverview,
  updateUnitAttendance,
} from "../../../../../service/course-management";
import { useParams } from "react-router-dom";
import {
  convertSeconds,
  convertToSeparateDateAndTime,
  formatDate,
  formatDateToIsoStringHHss,
} from "../../../../../utils/converDate";
import FormItemDropdown from "../../../../../components/form-dropdown/FormItemDropdown";
import { getCourseName } from "../../../../../service/courses";
import { useForm } from "antd/es/form/Form";
import SafeLink from "../../../../../components/link/SafeLink";
import { routesConfig } from "../../../../../config/routes";
import { sanitizeExpression } from "../../../../../utils/functionUltils";

type Props = {};

const imgPath = "/images/";

const LearningProgress = (props: Props) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const { studentId, courseId } = useParams();
  const [loading, setLoading] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [loadingAttendanceHistory, setAttendanceHistory] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [studentInfo, setStudentInfo] = useState<any>([]);
  const [isAttendanceTab, setIsAttendanceTab] = useState(false);
  const [progressDetail, setProgressDetail] = useState<any>([]);
  const [attendanceData, setAttendanceData] = useState<any>([]);
  const [attendanceHistoryData, setAttendanceHistoryData] = useState<any>([]);
  const [onlineDateTime, setOnlineDateTime] = useState<any>({
    date: null,
    time: null,
  });
  const [detailData, setDetailData] = useState<any>(null);

  // Breadcrumb
  const listBreadcrumb = [
    {
      title: t("Quản lý khóa học", "Quản lý khóa học"),
    },
    {
      title: (
        <SafeLink to={`${routesConfig.courseManagement}/${courseId}/${routesConfig.courseStudentList}`}>
          <span>{detailData?.name}</span>
        </SafeLink>
      )
    },
    {
      title: t("Tiến độ học tập", "Tiến độ học tập"),
    },
  ];

  // Attendance result options
  const resultOptions = [
    {
      code: true,
      name: t("Có mặt", "Có mặt"),
    },
    {
      code: false,
      name: t("Vắng", "Vắng"),
    },
  ];

  // Data column
  const progressDataColumns: TableColumnsType<any> = [
    {
      title: t("Cấu trúc khoá học", "Cấu trúc khoá học"),
      dataIndex: "name",
      key: "name",
      width: 50,
      render: (text, record) => (
        <>
          {record.isCompleted && (
            <CheckCircleTwoTone
              twoToneColor={["#fff", "#52C41A"]}
              style={{ marginRight: 5 }}
            />
          )}
          {text}
        </>
      ),
    },
    {
      title: t("Nỗ lực mong chờ", "Nỗ lực mong chờ"),
      dataIndex: "expectedTime",
      key: "expectedTime",
      width: 20,
      render: (_: any, record: any) => (
        <span>{record.expectedTime == null
          ? null
          : `${record.expectedTime} ${record.expectedTimeType === 10 ? t("phút", "phút") : t("giờ", "giờ")}`}</span>
      ),
    },
    {
      title: t("Phiên học tập", "Phiên học tập"),
      children: [
        {
          title: t("Bắt đầu", "Bắt đầu"),
          dataIndex: "startLearningTime",
          key: "startLearningTime",
          width: 30,
          render: (_: any, record: any) => (
            <span style={{color: record.isFirstDoneTime ? "#52C41A" : "#000"}}>
              {record.startLearningTime == null
                ? null
                : formatDateToIsoStringHHss(record.startLearningTime)}
            </span>
          ),
          className: "learning-time-col__start",
        },
        {
          title: t("Kết thúc", "Kết thúc"),
          dataIndex: "endLearningTime",
          key: "endLearningTime",
          width: 30,
          render: (_: any, record: any) => (
            <span style={{color: record.isFirstDoneTime ? "#52C41A" : "#000"}}>
              {record.endLearningTime == null
                ? null
                : formatDateToIsoStringHHss(record.endLearningTime)}
            </span>
          ),
          className: "learning-time-col__end",
        },
      ],
      className: "learning-time-col",
    },
    {
      title: t("Điểm danh", "Điểm danh"),
      dataIndex: "attendance",
      key: "attendance",
      width: 20,
      render: (_: any, record: any) => (
        <Tag
          color={
            record.attendance == null
              ? "#fff"
              : record.attendance
              ? "#52C41A"
              : "red"
          }
        >
          <span>
            {record.attendance == null
              ? null
              : record.attendance
              ? t("Có mặt", "Có mặt")
              : t("Vắng", "Vắng")}
          </span>
        </Tag>
      ),
    },
  ];

  const attendanceDataColumns: TableColumnsType<any> = [
    {
      title: t("Cấu trúc khoá học", "Cấu trúc khoá học"),
      dataIndex: "name",
      key: "name",
      width: 40,
      render: (text, record) => (
        <>
          {record.isCompleted && (
            <CheckCircleTwoTone
              twoToneColor={["#fff", "#52C41A"]}
              style={{ marginRight: 5 }}
            />
          )}
          {text}
        </>
      ),
    },
    {
      title: t("Thời gian điểm danh", "Thời gian điểm danh"),
      dataIndex: "attendanceDate",
      key: "attendanceDate",
      width: 30,
      render: (_: any, record: any) => (
        <span>
          {record.attendanceDate == null
            ? null
            : formatDateToIsoStringHHss(record.attendanceDate)}
        </span>
      ),
    },
    {
      title: t("Kết quả điểm danh", "Kết quả điểm danh"),
      dataIndex: "isAttendance",
      key: "isAttendance",
      width: 20,
      render: (_: any, record: any) => (
        <>
          {isEditMode ? (
            <>
              {record.isAttendance == null ? null : (
                <Form.Item name={record.attendanceResultId}>
                  <FormItemDropdown
                    defaultValue={record.isAttendance}
                    style={{ maxWidth: 120 }}
                    options={resultOptions}
                  />
                </Form.Item>
              )}
            </>
          ) : (
            <span>
              {record.isAttendance == null
                ? null
                : record.isAttendance
                ? t("Có mặt", "Có mặt")
                : t("Vắng", "Vắng")}
            </span>
          )}
        </>
      ),
    },
    // {
    //   title: "",
    //   dataIndex: "review",
    //   key: "review",
    //   width: 10,
    //   render: (_: any, record: any) => (
    //     <a style={{ color: "#3C88FB" }}>{record.review ? t("Xem lại", "Xem lại") : null}</a>
    //   ),
    // },
  ];

  // render tag color
  const renderTagColor = (status: string) => {
    switch (status) {
      case "Đang học":
        return "#1677FF";
      case "Hết hạn":
        return "#FF4D4F";
      case "Hoàn thành":
        return "#52C41A";
      default:
        break;
    }
  };

  // Get attendance history
  const handleViewHistory = async () => {
    setAttendanceHistory(true);
    setOpenHistory(true);
    await getAttendanceHistory(Number(courseId), Number(studentId))
      .then((res) => {
        if (res.status === 200) {
          setAttendanceHistoryData(res.data.data);
          setTimeout(() => {
            setAttendanceHistory(false);
          }, 333);
        }
      })
      .finally(() => setAttendanceHistory(false));
  };

  // Dropdown's options
  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <a onClick={() => setIsEditMode(true)}>
          {" "}
          <EditOutlined /> {t("&nbsp; Sửa báo cáo", "&nbsp; Sửa báo cáo")}{" "}
        </a>
      ),
    },
    {
      key: "2",
      label: (
        <a onClick={() => handleExportAttendance()}>
          {" "}
          <ExportOutlined /> {t("&nbsp; Xuất báo cáo", "&nbsp; Xuất báo cáo")}
        </a>
      ),
    },
    {
      key: "3",
      label: (
        <a onClick={() => handleViewHistory()}>
          {" "}
          <HistoryOutlined /> {t("&nbsp; Lịch sử chỉnh sửa", "&nbsp; Lịch sử chỉnh sửa")}
        </a>
      ),
    },
  ];

  // Handle export attendance
  const handleExportAttendance = async () => {
    if (studentId && courseId) {
      await exportAttendanceStudentByCourse(Number(courseId), Number(studentId)).then((res) => {
        const today = new Date();
        const formattedDate = formatDate(today);

        if (res.status === 200) {
          const fileData = res.data;

          const blob = new Blob([fileData], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const downloadLink = document.createElement("a");
          downloadLink.href = URL.createObjectURL(blob);

          downloadLink.download = `Baocaodiemdanh_${studentInfo?.courseCode}_${sanitizeExpression(studentInfo?.studentName || "")}_${formattedDate}.xlsx`;

          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }
      });
    }
  };

  // Onclose Drawer
  const onClose = () => {
    setOpenHistory(false);
  };

  // Get student info
  const getStudentInfo = async () => {
    await studentProgressOverview({
      courseId: courseId,
      studentId: Number(studentId),
    }).then((res) => {
      if (res.status === 200) {
        setStudentInfo(res.data?.data);
      }
    });
  };

  // Get student progress detail
  const getStudentProgressDetail = async () => {
    setLoading(true);
    await studentProgressDetail({
      courseId: courseId,
      studentId: Number(studentId),
    })
      .then((res) => {
        if (res.status === 200) {
          const dataProgress = res.data?.data?.sections?.map(
            (section: any) => ({
              key: section.id,
              name: section.name,
              children: section.sequences?.map((sequence: any) => ({
                key: sequence.id,
                name: sequence.name,
                children: sequence.units?.map((unit: any) => ({
                  key: unit.id,
                  name: unit.name,
                  attendance: unit.attendance,
                  isCompleted: unit.isCompleted,
                  expectedTime: unit.expectedTime,
                  expectedTimeType: unit.expectedTimeType,
                  expectedTimeInSeconds: unit.expectedTimeInSeconds,
                  children: unit.learningSessions?.map((item: any) => ({
                    isFirstDoneTime: item.isFirstDoneTime,
                    startLearningTime: item.startLearningTime,
                    endLearningTime: item.endLearningTime,
                  })),
                })),
              })),
            })
          );
          setProgressDetail(dataProgress);
          setTimeout(() => {
            setLoading(false);
          }, 333);
        }
      })
      .finally(() => setLoading(false));
  };

  // Get attendance
  const getAttendance = async () => {
    setLoading(true);
    if (studentId) {
      await getAttendanceStudentByCourse(Number(courseId), Number(studentId))
        .then((res) => {
          if (res.status === 200) {
            const data = res.data?.data?.map((item: any) => ({
              key: item.id,
              name: item.name,
              children: item.sequences?.map((sequence: any) => ({
                key: sequence.id,
                name: sequence.name,
                children: sequence?.units?.map((unit: any) => ({
                  key: unit.id,
                  name: unit.name,
                  attendanceDate: unit.attendanceDate,
                  isAttendance: unit.isAttendance,
                  attendanceResultId: unit.attendanceResultId,
                  review: true,
                })),
              })),
            }));
            setAttendanceData(data);
            setTimeout(() => {
              setLoading(false);
            }, 333);
          }
        })
        .finally(() => setLoading(false));
    }
  };

  // Save edit attendance
  const onFinish = async (values: any) => {
    const formattedData = Object.keys(values).map((key) => ({
      id: Number(key),
      isAttendance: values[key],
    }))
    const payload = formattedData.filter((item) => item.isAttendance != null);
    if (payload.length > 0) {
      await updateUnitAttendance(payload).then((res) => {
        if(res.status === 200) {
          getAttendance();
          setIsEditMode(false);
          message.success(t("Chỉnh sửa kết quả điểm danh thành công", "Chỉnh sửa kết quả điểm danh thành công"));
        }
      })
    } else {
      setIsEditMode(false);
      message.success(t("Chỉnh sửa kết quả điểm danh thành công", "Chỉnh sửa kết quả điểm danh thành công"));
    }
  };

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  // Get all keys to expand
  const getAllKeys = (item: any) => {
    if (!item.children || item.children.length === 0) {
      return [item.key];
    }
    return [item.key, ...item.children.flatMap(getAllKeys)];
  };

  // useEffect
  useEffect(() => {
    getStudentInfo();
    getStudentProgressDetail();
  }, []);

  useEffect(() => {
    const getDetailCourseData = async () => {
      try {
        await getCourseName(Number(courseId)).then((res) => {
          const { data } = res.data;
          setDetailData(data);
        });
      } catch (error) {}
    };
    getDetailCourseData();
  }, [courseId]);

  useEffect(() => {
    if (isAttendanceTab) {
      getAttendance();
    }
  }, [isAttendanceTab]);

  useEffect(() => {
    if (progressDetail.length > 0) {
      const allKeys = progressDetail.flatMap(getAllKeys);
      setExpandedKeys(allKeys);
    }
  }, [progressDetail]);

  useEffect(() => {
    if (studentInfo?.lastOnlineAt) {
      const dateTime = convertToSeparateDateAndTime(studentInfo.lastOnlineAt);
      setOnlineDateTime(dateTime);
    }
  }, [studentInfo?.lastOnlineAt]);

  return (
    <Layout className="page-header-group student-learning-progress">
      <div className={`page-header-group--layout`}>
        <div className="table-header--wrapper justify-between pb-6">
          <div className="table-header--left">
            <PageHeader
              title={t("Tiến độ học tập", "Tiến độ học tập")}
              listBreadcrumb={listBreadcrumb}
              breadcrumb={true}
              arrowLeft={imgPath + "arrowLeft.svg"}
              // handleBack={() => setSelectedKey("2")}
            />
          </div>
        </div>
      </div>
      <Row className="learning-progress-row">
        <Col
          xl={{ span: 7 }}
          md={{ span: 24 }}
          sm={{ span: 24 }}
          xs={{ span: 24 }}
          className="student-profile"
        >
          <div className="student-profile__avatar">
            <Avatar size={96} src={studentInfo.avatar} />
          </div>
          <div className="student-profile__info">
            <div className="student-profile__info__name">
              {t("Học viên:", "Học viên:")} {studentInfo.studentName}
            </div>
            <div className="student-profile__info__email">
              Email: {studentInfo.studentEmail}
            </div>
            <div className="student-profile__info__status">
              {t("Trạng thái học tập:", "Trạng thái học tập:")}{" "}
              <Tag
                color={renderTagColor(studentInfo.learningStatusDescription)}
              >
                {studentInfo.learningStatusDescription}
              </Tag>
            </div>
          </div>
        </Col>
        <Col
          xl={{ span: 10 }}
          md={{ span: 24 }}
          sm={{ span: 24 }}
          xs={{ span: 24 }}
          className="study-time"
        >
          <div>
            {t("Thời gian trực tuyến gần nhất:", "Thời gian trực tuyến gần nhất:")}{" "}
            {onlineDateTime.date &&
              `${onlineDateTime.time} ${t("ngày", "ngày")} ${onlineDateTime.date} ${t("tại học liệu", "tại học liệu")} "${studentInfo.lastOnlineAtUnitName}"`}
          </div>
          <div>
            {t("Tổng thời gian học tập:", "Tổng thời gian học tập:")}{" "}
            {convertSeconds(studentInfo.totalLearningTime).hours} {t("giờ", "giờ")}{" "}
            {convertSeconds(studentInfo.totalLearningTime).minutes} {t("phút", "phút")}
          </div>
          <div>
            {t("Tổng thời gian học tập trung bình:", "Tổng thời gian học tập trung bình:")}{" "}
            {convertSeconds(studentInfo.avgLearningTime).hours} {t("giờ", "giờ")}{" "}
            {convertSeconds(studentInfo.avgLearningTime).minutes} {t("phút/ngày", "phút/ngày")}
          </div>
        </Col>
        <Col
          xl={{ span: 7 }}
          md={{ span: 24 }}
          sm={{ span: 24 }}
          xs={{ span: 24 }}
          className="class-info"
        >
          <div>{t("Lớp ghi danh:", "Lớp ghi danh:")} {studentInfo.enrollClassName}</div>
          <div>{t("Thời gian mở lớp:", "Thời gian mở lớp:")} {studentInfo?.enrollClassOpeningTime ? formatDateToIsoStringHHss(studentInfo?.enrollClassOpeningTime) : ""}</div>
          <div>
            {t("Điểm danh:", "Điểm danh:")}{" "}
            <a
              style={{ color: "#3C88FB" }}
              onClick={() => setIsAttendanceTab(true)}
            >
              {studentInfo.attendanceCount}/
              {studentInfo.numberOfTimeToAttendance} {t("lần thành công", "lần thành công")}
            </a>
          </div>
        </Col>
      </Row>
      {!isAttendanceTab ? (
        <div className="learning-report-tab">
          <div className="attendance-report-header">
            <Typography.Title  level={4}>
              {t("Báo cáo học tập chi tiết", "Báo cáo học tập chi tiết")}
            </Typography.Title>
          </div>
          <Table
            dataSource={progressDetail}
            columns={progressDataColumns}
            pagination={false}
            expandable={{
              expandedRowKeys: expandedKeys,
              onExpand: (expanded, record) => {
                setExpandedKeys((prevKeys) =>
                  expanded
                    ? [...prevKeys, record.key]
                    : prevKeys.filter((key) => key !== record.key)
                );
              },
            }}
            scroll={{ x: 1500, y: 800 }}
            bordered={false}
            loading={loading}
            className="attendance-report-table"
          />
        </div>
      ) : (
        <div className="attendance-tab">
          <div className="attendance-report-header">
            <div className="flex">
              <ArrowLeftOutlined className="mr-2" style={{cursor: "pointer"}} 
                onClick={() => {
                    setIsAttendanceTab(false);
                    setIsEditMode(false);
                  }
                }/>
              <Typography.Title style={{margin: 0}} level={4}>{t("Báo cáo điểm danh", "Báo cáo điểm danh")}</Typography.Title>
            </div> 
            <Dropdown
              menu={{ items }}
              placement="bottomRight"
              trigger={["click"]}
            >
              <Button className="btn btn-primary">{t("Tùy chọn", "Tùy chọn")}</Button>
            </Dropdown>
          </div>
          <Form form={form} name="edit-attendance-result-form" onFinish={onFinish}>
            <Table
              dataSource={attendanceData}
              columns={attendanceDataColumns}
              pagination={false}
              expandable={{
                expandedRowKeys: expandedKeys,
                onExpand: (expanded, record) => {
                  setExpandedKeys((prevKeys) =>
                    expanded
                      ? [...prevKeys, record.key]
                      : prevKeys.filter((key) => key !== record.key)
                  );
                },
              }}
              scroll={{ x: 1500, y: 800 }}
              bordered={false}
              loading={loading}
              className="attendance-report-table"
            />
          {isEditMode ? (
            <div className="edit-attendance-result flex">
              <Button className="btn-primary" htmlType="submit">
                {t("Lưu", "Lưu")}
              </Button>
              <Button
                className="btn-default"
                onClick={() => setIsEditMode(false)}
                >
                {t("Hủy", "Hủy")}
              </Button>
            </div>
          ) : null}
          </Form>
        </div>
      )}
      <Drawer
        title={t("Lịch sử chỉnh sửa", "Lịch sử chỉnh sửa")}
        open={openHistory}
        onClose={onClose}
        width={450}
        className="attendance-history-drawer"
      >
        <div className="attendance-history-drawer__content">
          {attendanceHistoryData.length > 0 &&
            attendanceHistoryData.map((item: any, index: number) => {
              return (
                <div className="flex">
                  <span className="mr-3">{index + 1}</span>
                  <span>
                    {item.action} {t("lúc", "lúc")} {formatDateToIsoStringHHss(item.date)} {t("bởi", "bởi")}{" "}
                    {item.createdByUserName}
                  </span>
                </div>
              );
            })}
        </div>
      </Drawer>
    </Layout>
  );
};

export default LearningProgress;
